<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6">

    <!-- 403 -->
    <div
      v-if="permissionsReady && !can.view"
      class="mb-6 bg-white border border-gray-200 rounded-2xl shadow-sm p-10 text-center"
      role="alert"
    >
      <div class="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 text-red-600 grid place-items-center">
        <svg viewBox="0 0 24 24" class="w-6 h-6">
          <path fill="currentColor" d="M11 7h2v6h-2zm0 8h2v2h-2z" />
          <path fill="currentColor" d="M1 21h22L12 2 1 21z" />
        </svg>
      </div>
      <h2 class="font-bold text-gray-900 mb-1">403 — No Access</h2>
      <p class="text-gray-600">You don't have permission to view this report.</p>
    </div>

    <!-- Content -->
    <template v-else-if="permissionsReady && can.view && can.access">
      <h1 class="text-[24px] font-bold text-gray-900 mb-12">
        Suppliers Report
      </h1>

      <DynamicReportForm
        :showTitle="false"
        :columns="2"
        stackAt="never"
        contentWidth="2xl"
        actionLabel="View Report"
        :loading="loading"
        :fields="fields"
        v-model="model"
        @submit="handleSubmit"
      />
    </template>

  </div>
</template>

<script setup>
import { ref, watchEffect, onMounted } from "vue"
import DynamicReportForm from "@/components/DynamicReportForm.vue"
import api from "@/api"
import Swal from "sweetalert2"
import { loadMyPermissions, hasAny } from "@/utils/permissions"

const model = ref({
  supplier_id: "",
  company_id:  "",
  from:        "",
  to:          "",
})

const loading = ref(false)

// ===== Permissions =====
const permissionsReady = ref(false)
const can = ref({ view: false, access: false })

// ===== Options =====
const suppliersOptions = ref([])
const companiesOptions = ref([])

onMounted(async () => {
  await loadMyPermissions(false)
  can.value.view   = hasAny(["suppliers_report.view"])
  can.value.access = hasAny(["suppliers_report.view"])
  permissionsReady.value = true

 try {
    const res = await api.get("/accounting/suppliers")
    const list = res.data?.data ?? res.data ?? []
    suppliersOptions.value = list.map(s => ({
      label: s.supplier_name ?? s.name ?? `Supplier #${s.id}`,
      value: s.id,
    }))
  } catch (e) {
    console.error("Error fetching suppliers:", e)
  }

  try {
    const res = await api.get("/accounting/companies")
    companiesOptions.value = (res.data ?? []).map(c => ({
      label: c.name ?? c.company_name,
      value: c.id,
    }))
  } catch (e) {
    console.error("Error fetching companies:", e)
  }
})

// ===== Fields =====
const fields = ref([
  {
    name: 'supplier_id', label: 'Supplier Name', type: 'select',
    placeholder: 'Select supplier', options: [],
  },
  {
    name: 'company_id', label: 'Company', type: 'select',
    placeholder: 'Select company', options: [],
  },
  { name: 'from', label: 'From', type: 'date' },
  { name: 'to',   label: 'To',   type: 'date' },
])

watchEffect(() => {
  fields.value[0].options = suppliersOptions.value
  fields.value[1].options = companiesOptions.value
})

// ===== Submit =====
async function handleSubmit(payload) {
  if (!can.value.view) return

  const params = new URLSearchParams()
  if (payload.supplier_id) params.append("supplier_id", payload.supplier_id)
  if (payload.company_id)  params.append("company_id",  payload.company_id)
  if (payload.from)        params.append("from_date",   payload.from)
  if (payload.to)          params.append("to_date",     payload.to)

  loading.value = true
  try {
    const res = await api.get(`/accounting/reports/suppliers/pdf?${params.toString()}`, {
      responseType: "blob",
    })
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }))
    window.open(url, "_blank")
  } catch (err) {
    console.error("Failed to generate report:", err)
    Swal.fire({ icon: "error", title: "Failed to generate report", toast: true, position: "top", timer: 2000, showConfirmButton: false })
  } finally {
    loading.value = false
  }
}
</script>
